import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { colors } from '../../theme';
import { Navigation } from '../../components/shared/Navigation';
import { EmailCaptureForm } from './components/EmailCaptureForm';
import { TrustIndicators } from './components/TrustIndicators';
import { CTAButton } from './components/CTAButton';
import { SectionHeader } from './components/SectionHeader';
import { BulletList } from './components/BulletList';
import { TestimonialCard } from './components/TestimonialCard';
import { StepCard } from './components/StepCard';
import { BenefitBullet } from './components/BenefitBullet';
import { ProgressBar } from './components/ProgressBar';
import { CounterCard } from './components/CounterCard';
import { useScrollThreshold } from '../../hooks/useScrollThreshold';
import { useCountUpAnimation } from '../../hooks/useCountUpAnimation';
import { useEmailSubmit } from '../../hooks/useEmailSubmit';
import { testimonials } from './data/testimonials';
import { howItWorksSteps } from './data/howItWorksSteps';
import { benefitBullets } from './data/benefitBullets';
import './LandingPage.css';

// =============================================================================
// CONFIGURATION: Alpha Access Spots
// =============================================================================
const ALPHA_SPOTS_FILLED = 4;
const ALPHA_TOTAL_SPOTS = 20;

// =============================================================================
// SECTION: Navigation with CTA
// =============================================================================
const LandingNavigation: React.FC = () => {
  const { isAboveThreshold } = useScrollThreshold();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Navigation>
      <CTAButton
        variant="contained"
        onClick={scrollToTop}
        sx={{
          opacity: isAboveThreshold ? 1 : 0,
          pointerEvents: isAboveThreshold ? 'auto' : 'none',
        }}
      >
        Request Early Access
      </CTAButton>
    </Navigation>
  );
};

// =============================================================================
// SECTION: Hero
// =============================================================================
const HeroSection: React.FC = () => {
  const { email, setEmail, handleSubmit, isSubmitting } = useEmailSubmit();

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: { xs: '120px 24px 80px', md: '120px 48px 96px' },
        backgroundColor: colors.deepNavy,
        color: colors.pureWhite,
      }}
    >
      <Container maxWidth="md">
        {/* Headline */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2.25rem', md: '3.5rem' },
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            marginBottom: '24px',
            color: colors.pureWhite,
          }}
        >
          Your Feed. Your Algorithm.
        </Typography>

        {/* Subheadline */}
        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.25rem' },
            color: colors.slate[400],
            maxWidth: '600px',
            margin: '0 auto 32px',
            lineHeight: 1.6,
          }}
        >
          Don't let big tech and legacy media decide what you see and think about what's happening in the world.
        </Typography>

        {/* Benefit Bullets */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: '12px', md: '32px' },
            marginBottom: '40px',
          }}
        >
          {benefitBullets.map((bullet, index) => (
            <BenefitBullet key={index} bullet={bullet} />
          ))}
        </Box>

        {/* Email Form */}
        <Box id="signup">
          <EmailCaptureForm
            email={email}
            onEmailChange={setEmail}
            onSubmit={handleSubmit}
            placeholder="Enter your email"
            isSubmitting={isSubmitting}
          />
        </Box>

        {/* Trust Indicators */}
        <TrustIndicators items={['Free during alpha', 'No credit card', 'Unsubscribe anytime']} />
      </Container>
    </Box>
  );
};

// =============================================================================
// SECTION: Problem/Agitation
// =============================================================================
const ProblemSection: React.FC = () => {
  const { count, counterRef } = useCountUpAnimation();

  return (
    <Box
      id="problem"
      sx={{
        padding: { xs: '64px 24px', md: '96px 48px' },
        backgroundColor: colors.pureWhite,
      }}
    >
      <Container maxWidth="md">
        {/* Section Header */}
        <SectionHeader>
          All Signal. <Box component="span" sx={{ textDecoration: 'line-through' }}>No Noise.</Box>
        </SectionHeader>

        {/* Problem Description */}
        <Box sx={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: colors.slate[600],
              marginBottom: '32px',
              lineHeight: 1.7,
            }}
          >
            Your attention is your most valuable asset.
            <br /><br />
            Stop giving it away for free.
          </Typography>

          {/* The Math */}
          <CounterCard
            count={count}
            title="2 hours per day of scrolling equals"
            subtitle="hours per year"
            footnote={
              <>
                That's <strong style={{ color: colors.pureWhite }}>30 days</strong> of your life. Every year.
                <br />
                Scrolling. Not shipping.
              </>
            }
            counterRef={counterRef}
          />

          {/* The Kicker */}
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: colors.slate[600],
              fontStyle: 'italic',
            }}
          >
            Social media algorithms are designed to maximize your screen time, not your productivity. <Box component="span" sx={{ fontWeight: 700 }}>You need a better system.</Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// =============================================================================
// SECTION: Social Proof
// =============================================================================
const SocialProofSection: React.FC = () => {

  return (
    <Box
      id="social-proof"
      sx={{
        padding: { xs: '64px 24px', md: '96px 48px' },
        backgroundColor: colors.pureWhite,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <SectionHeader marginBottom="64px">
          Founders Who Quit Scrolling
        </SectionHeader>

        {/* Testimonials */}
        <Grid container spacing={4} sx={{ marginBottom: '64px', justifyContent: 'center' }}>
          {testimonials.map((testimonial, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <TestimonialCard testimonial={testimonial} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

// =============================================================================
// SECTION: Differentiation
// =============================================================================
const DifferentiationSection: React.FC = () => {

  return (
    <Box
      id="differentiation"
      sx={{
        padding: { xs: '64px 24px', md: '96px 48px' },
        backgroundColor: colors.softWhite,
      }}
    >
      <Container maxWidth="md">
        {/* Section Header */}
        <SectionHeader marginBottom="24px">
          How does it work?
        </SectionHeader>

        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.125rem' },
            color: colors.slate[600],
            textAlign: 'center',
            marginBottom: '64px',
            lineHeight: 1.7,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>filtrd</Box> isn't another aggregator competing for attention.
          <br /><br />
          It's the opposite — designed to give your attention back.
        </Typography>

        {/* Step 0: You Control What Matters */}
        <Box
          sx={{
            backgroundColor: colors.deepNavy,
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.pureWhite,
              marginBottom: '12px',
              fontSize: '1.25rem',
            }}
          >
            You Control What Matters
          </Typography>
          <BulletList
            items={[
              '2-minute setup. Change anytime.',
              'Choose which X accounts to follow and what topics matter to you.',
              'Share your personal and professional goals for hyper-personalized insights.',
            ]}
            color="slate400"
          />
        </Box>

        {/* Then, for each digest label */}
        <Typography
          sx={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: colors.deepNavy,
            marginBottom: '32px',
            textAlign: 'center',
          }}
        >
          Then, for each digest, filtrd:
        </Typography>

        {/* Steps 1-5 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {howItWorksSteps.map((step, index) => (
            <StepCard key={index} step={step} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

// =============================================================================
// SECTION: Final CTA
// =============================================================================
const FinalCTASection: React.FC = () => {
  const { email, setEmail, handleSubmit, isSubmitting } = useEmailSubmit();

  return (
    <Box
      id="final-cta"
      sx={{
        padding: { xs: '64px 24px', md: '96px 48px' },
        backgroundColor: colors.deepNavy,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            padding: { xs: '32px 24px', md: '64px' },
            backgroundColor: colors.whiteAlpha[10],
            borderRadius: '24px',
            border: `1px solid ${colors.whiteAlpha[10]}`,
          }}
        >
          {/* Headline */}
          <SectionHeader color="pureWhite" marginBottom="16px">
            Alpha Access is Limited
          </SectionHeader>

          <Typography
            sx={{
              color: colors.slate[400],
              marginBottom: '32px',
            }}
          >
            We look forward to incorporating your feedback to make <Box component="span" sx={{ fontWeight: 700 }}>filtrd</Box> the best possible news source for founders who value their time and attention.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              color: colors.signalOrange,
              fontWeight: 600,
              marginBottom: '32px',
            }}
          >
            Ready to Start Focusing on What Matters?
          </Typography>

          {/* Email Form */}
          <EmailCaptureForm
            email={email}
            onEmailChange={setEmail}
            onSubmit={handleSubmit}
            placeholder="your@email.com"
            isSubmitting={isSubmitting}
          />

          {/* Trust Indicators */}
          <TrustIndicators items={['Free during alpha', '2-min setup', 'No credit card']} marginBottom="32px" />

          {/* Progress Bar */}
          <ProgressBar
            current={ALPHA_SPOTS_FILLED}
            total={ALPHA_TOTAL_SPOTS}
            label={`Alpha closes at ${ALPHA_TOTAL_SPOTS} users. {remaining} spots remaining.`}
          />
        </Box>
      </Container>
    </Box>
  );
};

// =============================================================================
// SECTION: Footer
// =============================================================================
const Footer: React.FC = () => {

  return (
    <Box
      component="footer"
      sx={{
        padding: '48px 24px',
        backgroundColor: colors.deepNavy,
        textAlign: 'center',
        borderTop: `1px solid ${colors.whiteAlpha[10]}`,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          color: colors.pureWhite,
          marginBottom: '8px',
        }}
      >
        filtrd.io
      </Typography>
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: colors.slate[500],
          marginBottom: '24px',
        }}
      >
        For founders who'd rather ship than scroll.
      </Typography>
      <Typography
        sx={{
          fontSize: '0.75rem',
          color: colors.slate[600],
        }}
      >
        © {new Date().getFullYear()} Fractal Labs. All rights reserved.
      </Typography>
    </Box>
  );
};

// =============================================================================
// MAIN: Landing Page Component
// =============================================================================
const LandingPage: React.FC = () => {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <LandingNavigation />
      <HeroSection />
      <ProblemSection />
      <DifferentiationSection />
      <SocialProofSection />
      <FinalCTASection />
      <Footer />
    </Box>
  );
};

export default LandingPage;
